import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ClipboardCheck, UserPlus, Check, RotateCcw, Building2, Search,
  FileText, Shield, Monitor, GraduationCap, Users, Download, Eye,
} from "lucide-react";
import api from "../lib/api";

const categoryIcons = {
  documents: FileText,
  compliance: Shield,
  it_setup: Monitor,
  training: GraduationCap,
  general: Users,
};

const categoryColors = {
  documents: "bg-blue-50 text-blue-700 border-blue-200",
  compliance: "bg-red-50 text-red-700 border-red-200",
  it_setup: "bg-violet-50 text-violet-700 border-violet-200",
  training: "bg-amber-50 text-amber-700 border-amber-200",
  general: "bg-green-50 text-green-700 border-green-200",
};

const Onboarding = () => {
  const { currentBusiness } = useAuth();
  const [processes, setProcesses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [letterOpen, setLetterOpen] = useState(false);
  const [letterPreview, setLetterPreview] = useState("");
  const [letterEmpId, setLetterEmpId] = useState(null);
  const [letterLoading, setLetterLoading] = useState(false);
  const [empDocCount, setEmpDocCount] = useState(0);

  useEffect(() => {
    if (currentBusiness?.id) fetchData();
  }, [currentBusiness?.id]);

  const fetchData = async () => {
    try {
      const [procRes, empRes] = await Promise.all([
        api.get(`/onboarding/processes?business_id=${currentBusiness.id}`),
        api.get(`/employees?business_id=${currentBusiness.id}`),
      ]);
      setProcesses(procRes.data);
      setEmployees(empRes.data);
    } catch { toast.error("Failed to load onboarding data"); }
    finally { setLoading(false); }
  };

  const startOnboarding = async () => {
    if (!selectedEmployee) { toast.error("Select an employee"); return; }
    try {
      await api.post("/onboarding/processes", {
        employee_id: selectedEmployee,
        business_id: currentBusiness.id,
      });
      toast.success("Onboarding started");
      setDialogOpen(false);
      setSelectedEmployee("");
      fetchData();
    } catch { toast.error("Failed to start onboarding"); }
  };

  const toggleItem = async (processId, index, completed) => {
    try {
      const endpoint = completed ? "uncomplete" : "complete";
      await api.put(`/onboarding/processes/${processId}/items/${index}/${endpoint}`);
      // Refresh the detail
      const res = await api.get(`/onboarding/processes/${processId}`);
      setSelectedProcess(res.data);
      setProcesses(prev => prev.map(p => p.id === processId ? res.data : p));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update item");
    }
  };

  const handleGenerateLetter = async (employeeId) => {
    setLetterEmpId(employeeId);
    setLetterLoading(true);
    setLetterOpen(true);
    try {
      const res = await api.post(`/appointment-letter/preview/${employeeId}`, {
        business_id: currentBusiness.id,
      });
      setLetterPreview(res.data.rendered);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate letter"); setLetterOpen(false); }
    finally { setLetterLoading(false); }
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await api.get(`/appointment-letter/pdf/${letterEmpId}?business_id=${currentBusiness.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Appointment_Letter.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("Failed to download PDF"); }
  };

  const handleSaveEditedLetter = async () => {
    // Save the edited letter as a custom template for this preview
    toast.success("Letter content updated in preview");
  };

  const getEmpName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : "Unknown";
  };

  const getProgress = (process) => {
    const items = process.items || [];
    if (items.length === 0) return 0;
    return Math.round((items.filter(i => i.completed).length / items.length) * 100);
  };

  const filtered = processes.filter(p => {
    const name = getEmpName(p.employee_id).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  const activeCount = processes.filter(p => p.status === "in_progress").length;
  const completedCount = processes.filter(p => p.status === "completed").length;
  const totalEmployees = employees.length;
  // Employees who have at least one onboarding process
  const onboardedEmpIds = new Set(processes.filter(p => p.status === "completed").map(p => p.employee_id));
  const inProgressEmpIds = new Set(processes.filter(p => p.status === "in_progress").map(p => p.employee_id));
  const fullyOnboarded = onboardedEmpIds.size;
  const inProgressCount = inProgressEmpIds.size;
  const allOnboardedIds = new Set([...onboardedEmpIds, ...inProgressEmpIds]);
  const notStarted = totalEmployees - allOnboardedIds.size;
  const overallProgress = totalEmployees > 0 ? Math.round((fullyOnboarded / totalEmployees) * 100) : 0;

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Onboarding</h1>
          <p className="text-slate-500">Track new employee onboarding progress</p>
        </div>
        <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="start-onboarding-btn">
          <UserPlus className="h-4 w-4 mr-2" />
          Start Onboarding
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="stat-total-employees">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-100"><Users className="h-6 w-6 text-blue-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{totalEmployees}</p><p className="text-sm text-slate-500">Total Employees</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-onboarded">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-green-100"><Check className="h-6 w-6 text-green-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{fullyOnboarded}</p><p className="text-sm text-slate-500">Onboarded</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-in-progress">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-100"><UserPlus className="h-6 w-6 text-amber-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{inProgressCount}</p><p className="text-sm text-slate-500">In Progress</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card" data-testid="stat-not-started">
          <CardContent className="p-0">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-red-100"><ClipboardCheck className="h-6 w-6 text-red-700" /></div>
              <div><p className="text-2xl font-bold text-slate-800">{notStarted}</p><p className="text-sm text-slate-500">Not Started</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overall Progress */}
      <Card className="card-soft" data-testid="overall-progress">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-700">Overall Onboarding Progress</p>
            <p className="text-sm font-bold text-slate-800">{overallProgress}%</p>
          </div>
          <Progress value={overallProgress} className="h-3" />
          <p className="text-xs text-slate-400 mt-1.5">{fullyOnboarded} of {totalEmployees} employees fully onboarded</p>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Search by employee name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 input-field" data-testid="onboarding-search" />
      </div>

      {/* Process Cards */}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500">No onboarding processes found. Start one for a new hire.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((proc) => {
            const progress = getProgress(proc);
            const completedItems = (proc.items || []).filter(i => i.completed).length;
            const totalItems = (proc.items || []).length;
            return (
              <Card key={proc.id} className="card-soft cursor-pointer hover:shadow-md transition-shadow"
                onClick={async () => {
                  setSelectedProcess(proc);
                  setDetailOpen(true);
                  // Fetch document count for this employee
                  try {
                    const docs = await api.get(`/documents?employee_id=${proc.employee_id}&business_id=${currentBusiness.id}`);
                    setEmpDocCount(Array.isArray(docs.data) ? docs.data.length : 0);
                  } catch { setEmpDocCount(0); }
                }}
                data-testid={`onboarding-card-${proc.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-800">{getEmpName(proc.employee_id)}</h3>
                      <p className="text-xs text-slate-400">Started {new Date(proc.started_at).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="outline" className={proc.status === "completed" ? "bg-green-50 text-green-700 border-green-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                      {proc.status === "completed" ? "Completed" : "In Progress"}
                    </Badge>
                  </div>
                  <Progress value={progress} className="h-2 mb-2" />
                  <p className="text-xs text-slate-500">{completedItems}/{totalItems} items completed ({progress}%)</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Start Onboarding Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">Start Onboarding</DialogTitle>
            <DialogDescription>Select an employee to begin their onboarding process</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="input-field" data-testid="onboarding-employee-select">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              A default onboarding checklist with 14 items will be assigned (documents, compliance, IT setup, training). Items marked as optional can be skipped.
            </div>
            <Button onClick={startOnboarding} className="w-full btn-primary" data-testid="confirm-start-onboarding">
              Start Onboarding
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" onCloseAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedProcess ? getEmpName(selectedProcess.employee_id) : ""} — Onboarding
            </DialogTitle>
            <DialogDescription>
              {selectedProcess && `${(selectedProcess.items || []).filter(i => i.completed).length}/${(selectedProcess.items || []).length} items completed`}
            </DialogDescription>
          </DialogHeader>
          {selectedProcess && (
            <div className="space-y-2 mt-2" data-testid="onboarding-checklist">
              {(selectedProcess.items || []).map((item, idx) => {
                const CatIcon = categoryIcons[item.category] || ClipboardCheck;
                const isDocItem = item.category === "documents" && !item.optional;
                const docBlocked = isDocItem && !item.completed && empDocCount === 0;
                return (
                  <div key={`checklist-${item.label}-${idx}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      item.completed ? "bg-green-50/50 border-green-200" :
                      docBlocked ? "bg-amber-50/30 border-amber-200" :
                      "bg-white border-slate-200 hover:border-primary/30"
                    }`}
                    data-testid={`checklist-item-${idx}`}>
                    <button
                      onClick={() => toggleItem(selectedProcess.id, idx, item.completed)}
                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                        item.completed ? "bg-green-500 border-green-500 text-white" :
                        docBlocked ? "border-amber-300 cursor-not-allowed opacity-60" :
                        "border-slate-300 hover:border-primary"
                      }`}
                      data-testid={`toggle-item-${idx}`}>
                      {item.completed && <Check className="h-3.5 w-3.5" />}
                    </button>
                    <CatIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${item.completed ? "text-green-700 line-through" : "text-slate-800"}`}>{item.name}</p>
                      {item.description && <p className="text-xs text-slate-400">{item.description}</p>}
                      {docBlocked && (
                        <p className="text-[10px] text-amber-600 mt-0.5">Upload documents first to complete this item</p>
                      )}
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${categoryColors[item.category] || categoryColors.general}`}>
                      {item.category}
                    </Badge>
                    {item.optional && (
                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-400 border-slate-200">
                        optional
                      </Badge>
                    )}
                    {item.name === "Appointment Letter Generation" && !item.completed && (
                      <Button size="sm" variant="outline"
                        className="h-6 text-[10px] rounded-full border-primary/30 text-primary hover:bg-primary/5 px-2"
                        onClick={(e) => { e.stopPropagation(); handleGenerateLetter(selectedProcess.employee_id); }}
                        data-testid={`generate-letter-${idx}`}>
                        <Eye className="h-3 w-3 mr-1" /> Generate
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Appointment Letter Preview Dialog */}
      <Dialog open={letterOpen} onOpenChange={setLetterOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Appointment Letter Preview</DialogTitle>
            <DialogDescription>Review and edit before downloading as PDF</DialogDescription>
          </DialogHeader>
          {letterLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>
          ) : (
            <div className="space-y-4 mt-2">
              <Textarea
                value={letterPreview}
                onChange={(e) => setLetterPreview(e.target.value)}
                className="input-field min-h-[400px] font-mono text-sm leading-relaxed"
                data-testid="letter-preview-editor"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" className="rounded-full" onClick={() => setLetterOpen(false)}>
                  Close
                </Button>
                <Button className="btn-primary rounded-full" onClick={handleDownloadPdf} data-testid="download-letter-pdf">
                  <Download className="h-4 w-4 mr-2" /> Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Onboarding;
